import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useCheckInTemplates,
  CheckInTemplate,
  CheckInCategory,
  CHECK_IN_CATEGORIES,
  FREQUENCY_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
} from "@/hooks/use-check-in-templates";
import { useClientCheckIns } from "@/hooks/use-client-check-ins";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  ClipboardList,
  HeartPulse,
  Utensils,
  TrendingUp,
  Bandage,
  Brain,
  Settings,
  FileQuestion,
  Search,
} from "lucide-react";

const CATEGORY_ICONS: Record<CheckInCategory, React.ReactNode> = {
  general: <ClipboardList className="h-4 w-4" />,
  wellness: <HeartPulse className="h-4 w-4" />,
  nutrition: <Utensils className="h-4 w-4" />,
  progress: <TrendingUp className="h-4 w-4" />,
  injury: <Bandage className="h-4 w-4" />,
  mindset: <Brain className="h-4 w-4" />,
  custom: <Settings className="h-4 w-4" />,
};

interface AssignCheckInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  existingTemplateIds: string[];
  onSuccess: () => void;
}

type Step = "select" | "configure";

export function AssignCheckInDialog({
  open,
  onOpenChange,
  clientId,
  existingTemplateIds,
  onSuccess,
}: AssignCheckInDialogProps) {
  const { templates, loading: loadingTemplates } = useCheckInTemplates();
  const { assignCheckIn } = useClientCheckIns(clientId);
  const [step, setStep] = useState<Step>("select");
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<CheckInTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Configuration form state
  const [frequency, setFrequency] = useState<"one_time" | "daily" | "weekly" | "biweekly" | "monthly">("weekly");
  const [dayOfWeek, setDayOfWeek] = useState<number>(1); // Monday
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [trainerNotes, setTrainerNotes] = useState("");
  const [sendReminder, setSendReminder] = useState(true);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("select");
      setSelectedTemplate(null);
      setSearch("");
      setFrequency("weekly");
      setDayOfWeek(1);
      setDayOfMonth(1);
      setStartDate(new Date().toISOString().split("T")[0]);
      setTrainerNotes("");
      setSendReminder(true);
    }
  }, [open]);

  // Filter templates
  const availableTemplates = templates.filter(
    (t) =>
      t.is_active &&
      !existingTemplateIds.includes(t.id) &&
      (t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.description?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleSelectTemplate = (template: CheckInTemplate) => {
    setSelectedTemplate(template);
    setStep("configure");
  };

  const handleBack = () => {
    setStep("select");
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      await assignCheckIn({
        template_id: selectedTemplate.id,
        client_id: clientId,
        frequency,
        day_of_week: (frequency === "weekly" || frequency === "biweekly") ? dayOfWeek : undefined,
        day_of_month: frequency === "monthly" ? dayOfMonth : undefined,
        start_date: startDate,
        trainer_notes: trainerNotes || undefined,
        send_reminder: sendReminder,
      });
      toast.success(`Assigned "${selectedTemplate.name}" to client`);
      onSuccess();
    } catch (error) {
      toast.error("Failed to assign check-in");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {step === "select" ? "Assign check-in" : "Configure schedule"}
          </DialogTitle>
          <DialogDescription>
            {step === "select"
              ? "Select a check-in template to assign to this client"
              : `Configure when "${selectedTemplate?.name}" should be due`}
          </DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Template List */}
            <ScrollArea className="flex-1 -mx-6 px-6 min-h-[300px]">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availableTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {templates.length === 0
                      ? "No check-in templates available. Create one first."
                      : existingTemplateIds.length === templates.filter((t) => t.is_active).length
                      ? "All templates are already assigned to this client."
                      : "No templates match your search."}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 py-2">
                  {availableTemplates.map((template) => {
                    const category = (template.category as CheckInCategory) || "general";
                    const categoryMeta = CHECK_IN_CATEGORIES[category];

                    return (
                      <Card
                        key={template.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div
                              className={`h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0 ${categoryMeta.color}`}
                            >
                              {CATEGORY_ICONS[category]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{template.name}</div>
                              {template.description && (
                                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                  {template.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs gap-1">
                                  <FileQuestion className="h-3 w-3" />
                                  {template.question_count || 0} questions
                                </Badge>
                                {template.is_system_template && (
                                  <Badge variant="outline" className="text-xs">
                                    Template
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <>
            {/* Back Button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-fit -mt-2"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to templates
            </Button>

            <div className="space-y-4">
              {/* Frequency */}
              <div className="space-y-2">
                <Label>Frequency</Label>
                <RadioGroup
                  value={frequency}
                  onValueChange={(value) => setFrequency(value as typeof frequency)}
                  className="grid grid-cols-2 gap-2"
                >
                  {FREQUENCY_OPTIONS.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Label htmlFor={option.value} className="font-normal cursor-pointer">
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Day of Week (for weekly/biweekly) */}
              {(frequency === "weekly" || frequency === "biweekly") && (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select
                    value={dayOfWeek.toString()}
                    onValueChange={(value) => setDayOfWeek(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAY_OF_WEEK_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Day of Month (for monthly) */}
              {frequency === "monthly" && (
                <div className="space-y-2">
                  <Label>Day of Month</Label>
                  <Select
                    value={dayOfMonth.toString()}
                    onValueChange={(value) => setDayOfMonth(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Start Date */}
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              {/* Send Reminder */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Send reminders</Label>
                  <p className="text-xs text-muted-foreground">
                    Notify client when check-in is due
                  </p>
                </div>
                <Switch
                  checked={sendReminder}
                  onCheckedChange={setSendReminder}
                />
              </div>

              {/* Trainer Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Private notes about this assignment..."
                  value={trainerNotes}
                  onChange={(e) => setTrainerNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Assign Check-in
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
